/**
 * platform/ai.ts — AI Intelligence request and shapes (staff-only,
 * `platform:operations.observe`). The API returns counts and closed tokens
 * only (`apps/api/src/domains/system/platformAi.ts`); nothing here can carry a
 * prompt, answer or tool argument.
 */
import type { ConsoleRequest } from './requests.js';

export const AI_WINDOWS = [7, 30] as const;
export type AiWindow = (typeof AI_WINDOWS)[number];

export const aiRequests = {
  overview: (windowDays: AiWindow): ConsoleRequest => ({
    method: 'GET',
    path: `/api/system/ai/overview?windowDays=${windowDays}`,
  }),
};

/** Every request shape this module builds — walked by the nginx binding test. */
export function allAiRequestShapes(): ConsoleRequest[] {
  return AI_WINDOWS.map((w) => aiRequests.overview(w));
}

type Route = 'deterministic' | 'jev' | 'reasoning' | 'human';
export type Instrumentation = 'measured' | 'not_captured' | 'logs_only';

export interface AiSummary {
  readonly runs: {
    readonly total: number;
    readonly byStatus: Readonly<Record<string, number>>;
    readonly runtimeMs: { p50: number | null; p95: number | null; finished: number };
    readonly timelineErrors: number;
  };
  readonly actions: { readonly total: number; readonly byResult: Readonly<Record<string, number>> };
  readonly usage: {
    readonly records: number;
    readonly inputTokens: number;
    readonly outputTokens: number;
    readonly toolCalls: number;
    readonly connectorCalls: number;
    readonly retries: number;
    readonly modelRecords: number;
    readonly modelRecordsWithoutCost: number;
    readonly modelCostUsd: number | null;
  };
  readonly decisions: {
    readonly n: number;
    readonly judged: number;
    readonly totalCostUsd: number;
    readonly routeShare: Readonly<Record<Route, number | null>>;
    readonly resolvedWithoutReasoning: number | null;
    readonly jevEscalationRate: number | null;
    readonly humanOverrideRate: number | null;
    readonly costPerSuccessfulUsd: number | null;
    readonly accuracyByRoute: Readonly<Record<Route, { judged: number; accuracy: number | null }>>;
    readonly latencyByRoute: Readonly<Record<Route, { p50: number | null; p95: number | null }>>;
  };
  readonly phoebe: {
    readonly cases: number;
    readonly turns: number;
    readonly sessions: Readonly<Record<string, number>>;
    readonly endReasons: Readonly<Record<string, number>>;
  };
}

export interface AiOverviewRead {
  readonly windowDays: AiWindow;
  readonly since: string;
  readonly measuredAt: string;
  readonly instrumentation: Readonly<Record<string, Instrumentation>>;
  readonly platform: AiSummary;
  readonly organizations: readonly (
    | ({ readonly id: string; readonly name: string; readonly state: 'read' } & AiSummary)
    | { readonly id: string; readonly name: string; readonly state: 'unreadable' }
  )[];
}

const TERMINAL = ['COMPLETED', 'FAILED', 'CANCELLED', 'BUDGET_EXHAUSTED', 'POLICY_BLOCKED'];

/** Failure and blocked rates over FINISHED runs; null when none finished (not 0%). */
export function runRates(s: AiSummary): {
  finished: number;
  failed: number | null;
  blocked: number | null;
} {
  const b = s.runs.byStatus;
  const finished = TERMINAL.reduce((a, k) => a + (b[k] ?? 0), 0);
  const of = (n: number) => (finished === 0 ? null : n / finished);
  return {
    finished,
    failed: of(b.FAILED ?? 0),
    blocked: of((b.POLICY_BLOCKED ?? 0) + (b.BUDGET_EXHAUSTED ?? 0)),
  };
}

export const pct = (r: number | null): string => (r === null ? '—' : `${Math.round(r * 100)}%`);
export const ms = (n: number | null): string =>
  n === null ? '—' : n >= 1000 ? `${(n / 1000).toFixed(1)} s` : `${Math.round(n)} ms`;
export const count = (n: number): string => n.toLocaleString('en-US');

/** Model cost as the ledger can honestly state it: unknown is never $0. */
export function modelCost(u: AiSummary['usage']): string {
  if (u.modelRecords === 0 && u.modelCostUsd === null) return 'no model calls';
  const known = u.modelCostUsd === null ? null : `$${u.modelCostUsd.toFixed(2)}`;
  if (u.modelRecordsWithoutCost === 0) return known ?? 'unknown';
  return known
    ? `${known} + ${u.modelRecordsWithoutCost} unknown`
    : `unknown (${u.modelRecordsWithoutCost} calls)`;
}

/** What each instrumentation key means, for the "not captured" list. */
export const INSTRUMENTATION_TEXT: Record<string, string> = {
  agentRuns: 'Agent runs by status and runtime',
  agentActions: 'Agent tool-call outcomes',
  agentTokens: 'Agent tokens and retries',
  agentModelCost: 'Agent model cost',
  decisions: 'Routed decisions (decision ledger)',
  decisionEvaluation: 'Decision accuracy and override (labelled outcomes)',
  phoebeSessions: 'Phoebe sessions and turns',
  phoebeTokens: 'Phoebe (virtual workforce) token usage',
  phoebeTurnLatency: 'Phoebe per-turn latency',
  intelligenceTurnTokens: 'Intelligence turn tokens and latency',
  providerErrors: 'Model provider errors by kind',
};
